import { useMemo, useState, useEffect, useRef } from 'react';
import {
  Grid, TextField, MenuItem, Switch, FormControlLabel, Typography, Alert, Box,
  InputAdornment, IconButton, Stack, Tooltip, Autocomplete,
} from '@mui/material';
import { AutoFixHigh } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import FormFileUpload from '../ui/forms/FormFileUpload';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneNumberField from '../ui/forms/PhoneNumberField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import SearchableMasterSelect from './SearchableMasterSelect';
import SearchableMasterMultiSelect from './SearchableMasterMultiSelect';
import ParentMasterSelect from './ParentMasterSelect';
import RestaurantSlugSelect from './RestaurantSlugSelect';
import ZoneMasterSelect from './ZoneMasterSelect';
import HubCoordinatePicker, { isTransportHubType } from './HubCoordinatePicker';
import { GEO_FIELD_SOURCE } from '../../constants/masterSetupFlow';
import { getMasterTypeForField, LEGACY_PARENT_META_KEY } from '../../constants/masterRefFields';
import { ON_DEMAND_AUTO_CODE_TYPES, ON_DEMAND_HIDE_STATUS_TOGGLE_TYPES, ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES, ON_DEMAND_INTEGER_SORT_ORDER_TYPES, ON_DEMAND_UNIQUE_SORT_ORDER_TYPES, ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES, ON_DEMAND_DESCRIPTION_VALIDATED_TYPES, slugifyMasterCode } from '../../constants/onDemandMasters';
import { FOOD_AUTO_CODE_TYPES, FOOD_DESCRIPTION_VALIDATED_TYPES, FOOD_HIDE_STATUS_TOGGLE_TYPES, FOOD_HIDE_PARENT_PICKER_TYPES, FOOD_IMAGE_IN_BASIC_INFO_TYPES, FOOD_LETTER_ONLY_NAME_TYPES, FOOD_LIST_STATUS_TOGGLE_TYPES, FOOD_SEQUENTIAL_SORT_ORDER_TYPES, FOOD_INTEGER_SORT_ORDER_TYPES, FOOD_UNIQUE_SORT_ORDER_TYPES, FOOD_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES, FOOD_METADATA_DESCRIPTION_TYPES, FOOD_PARENT_IN_BASIC_INFO_TYPES, PARENT_CATEGORY_MASTER_TYPES, FOOD_UNIT_TYPE_SUGGESTIONS, KITCHEN_STATION_TYPE_SUGGESTIONS, getNextFoodMasterSortOrder, getNextFoodSubCategorySortOrder, getNextFoodAttributeValueSortOrder, validateDuplicateFoodSubCategoryCode, restaurantHasMainBranch, slugifyFoodMasterCode, formatPreparationTimeName, preparationTimeCodeFromMinutes, formatFoodTaxGroupName, normalizeFoodUnitType, formatFoodUnitTypeLabel, normalizeKitchenStationType, formatKitchenStationTypeLabel } from '../../constants/foodMasterRules';
import {
  DELIVERY_AUTO_CODE_TYPES,
  DELIVERY_DESCRIPTION_VALIDATED_TYPES,
  DELIVERY_HIDE_STATUS_TOGGLE_TYPES,
  DELIVERY_STATUS_TOGGLE_LIST_TYPES,
  DELIVERY_INTEGER_SORT_ORDER_TYPES,
  DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES,
  DELIVERY_UNIQUE_SORT_ORDER_TYPES,
  DELIVERY_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES,
  formatDeliveryPartnerPricingDescription,
  formatDeliveryPartnerPricingExample,
  formatDeliveryPartnerPricingGuaranteeHint,
  formatDeliveryPartnerPricingName,
  formatDeliveryIncentiveExplanation,
  formatDeliveryPartnerTierExplanation,
  formatSubstitutionRuleExplanation,
  isDeliveryIncentivePeakHour,
  isPlaceholderDeliveryPartnerPricingDescription,
  isPlaceholderDeliveryPartnerPricingName,
  resolvePackagingSampleImageUrl,
  resolveGroceryCategorySampleImageUrl,
  resolveGrocerySubCategorySampleImageUrl,
  getNextGrocerySubCategorySortOrder,
  validateDeliveryPartnerPricingDescription,
  groceryProductImageConstraintsForType,
  resolveGroceryProductSampleImageUrl,
} from '../../constants/deliveryMasterRules';
import {
  formatOpeningHourDayLabel,
  isOpeningHourClosed,
  openingHourDayTaken,
  allOpeningHourDaysTaken,
  sanitizeOpeningHourTimeInput,
  sortOrderForOpeningHourDay,
} from '../../utils/restaurantOpeningHours';
import {
  resolveFoodCategorySampleImageUrl,
  resolveCuisineSampleImageUrl,
  resolveFoodAddonSampleImageUrl,
  resolveFoodAddonImagePreviewUrl,
} from '../../utils/foodMasterImages';
import { resolveDietaryIconUrl, resolveDietarySampleImageUrl } from '../../utils/dietaryMasterIcons';
import { TRANSPORT_AUTO_CODE_TYPES, TRANSPORT_LETTER_ONLY_NAME_TYPES, TRANSPORT_HIDE_STATUS_TOGGLE_TYPES, TRANSPORT_HIDE_DESCRIPTION_TYPES, TRANSPORT_SEQUENTIAL_SORT_ORDER_TYPES, TRANSPORT_INTEGER_SORT_ORDER_TYPES, TRANSPORT_UNIQUE_SORT_ORDER_TYPES, TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES, TRANSPORT_DESCRIPTION_VALIDATED_TYPES, filterRecordsForParentScopedSortOrder, PASSENGER_CAPACITY_MIN, PASSENGER_CAPACITY_MAX, isVehiclePassengerCapacityField, sanitizePassengerCapacityInput, validatePassengerCapacity, validateVehicleServiceTypes, isRideTagColorField, sanitizeRideTagColorInput, validateRideTagColor, RIDE_TAG_COLOR_PATTERN, parseCommaSeparatedDayValues, formatCommaSeparatedDayValues, isMasterRecordDeprecated } from '../../constants/transportSubserviceMasters';
import { COMMON_MANAGED_MASTER_TYPES, COMMON_LETTER_ONLY_NAME_TYPES } from '../../constants/commonManagedMasters';
import { isAssetUrlField } from '../../utils/assetUrl';
import { chargeFieldLabel, getCurrencySymbol, isCurrencyField, formatCurrencyInputValue, sanitizeCurrencyInput, stripCurrencyLabelSuffix } from '../../utils/currency';
import { resolveMasterImageConstraints } from '../../utils/masterImageConstraints';
import { validateMasterForm, isMasterFormValid, summarizeMasterFormErrors, type FieldErrors, validateDuplicateMasterCode, validateDuplicateSortOrder, validateDuplicateSkillName } from '../../utils/masterFormValidation';
import {
  validateOptionalDescription,
  limitConsecutiveRepeatedCharacters,
} from '../../utils/descriptionValidation';
import type { MasterSchema, MasterFieldDef } from '../../constants/masters';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

const MASTER_CODE_PLACEHOLDERS: Record<string, string> = {
  trip_types: 'LOCAL',
  vehicle_types: 'SEDAN',
  ride_attributes: 'AC',
  ride_attribute_values: 'AC_YES',
  ride_tags: 'FEATURED',
  service_availability_schedules: 'WEEKDAY_MORNING',
  food_availability_schedules: 'weekday_lunch',
  food_images: 'biryani_thumb',
  delivery_slots: 'lunch_slot',
  packaging_types: 'eco_packaging',
  grocery_store_types: 'hypermarket',
  grocery_categories: 'fruits',
  grocery_sub_categories: 'apple',
  grocery_brands: 'amul',
  warehouses: 'wh_blr_north',
  product_variants: '500g',
  weight_types: 'kg',
  suppliers: 'metro_wholesale',
  stock_alerts: 'critical',
  grocery_tax_groups: 'gst_5',
  taxes: 'gst_18',
  units: 'kilogram',
  rejection_reasons: 'pickup_too_far',
  delivery_modes: 'express_delivery',
  grocery_product_images: 'milk_1l_thumb',
  grocery_availability_schedules: 'weekday_morning',
  delivery_partner_pricing: 'world_food_partner_pricing',
  delivery_incentives: 'peak_hour_food',
  delivery_partners_tiers: 'bronze',
  merchant_document_types: 'gst_certificate',
  cancellation_reasons: 'changed_mind',
  substitution_rules: 'allow_similar',
  service_categories: 'ELECTRICIAN',
  skills: 'WIRING',
  certificates: 'ELECTRICIAN_LICENSE',
  experience_levels: 'SENIOR',
  pricing_templates: 'FIXED_VISIT',
  visit_charges: 'STANDARD_VISIT',
  consultation_types: 'MEDICAL',
  organizer_types: 'VENUE',
};

/** Types that use primary→sub self-hierarchy (parent optional for primary). */
const OPTIONAL_PARENT_TYPES = new Set(['service_categories', 'food_categories', 'categories']);

function masterCodePlaceholder(type: string): string {
  return MASTER_CODE_PLACEHOLDERS[type] || 'UNIQUE_CODE';
}

function AutoCodeAdornment({
  auto,
  disabled,
  onAutoChange,
  onRegenerate,
}: {
  auto: boolean;
  disabled?: boolean;
  onAutoChange: (on: boolean) => void;
  onRegenerate: () => void;
}) {
  return (
    <InputAdornment position="end">
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Tooltip title={auto
          ? 'Regenerate code from name (or generate a new code if name is empty)'
          : 'Generate code from name — turns Auto ON'}>
          <span>
            <IconButton
              size="small"
              edge="end"
              aria-label="Generate or regenerate code"
              disabled={disabled}
              onClick={onRegenerate}
            >
              <AutoFixHigh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <FormControlLabel
          sx={{ mr: 0, ml: 0.25 }}
          control={(
            <Switch
              size="small"
              checked={auto}
              onChange={(e) => onAutoChange(e.target.checked)}
              inputProps={{ 'aria-label': auto ? 'Turn Auto code OFF' : 'Turn Auto code ON' }}
            />
          )}
          label="Auto"
        />
      </Stack>
    </InputAdornment>
  );
}

export interface MasterFormState {
  code: string;
  name: string;
  description: string;
  country: string;
  state: string;
  city: string;
  district: string;
  parentId: string;
  sortOrder: number;
  isActive: boolean;
  metadata: Record<string, string | number | boolean>;
}

interface ParentOption {
  _id: string;
  name: string;
  code: string;
}

interface Props {
  open: boolean;
  editing: boolean;
  schema?: MasterSchema | null;
  schemaLoading?: boolean;
  currentType: string;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  parentMasterType?: string;
  parentOptions?: ParentOption[];
  /** When set on create, sort order is locked to this sequential value. */
  lockedSortOrder?: number | null;
  /** Existing records for duplicate code validation on create/edit. */
  existingRecords?: Array<{ _id?: string; code?: string; name?: string; sortOrder?: number; parentId?: string; attributeCode?: string; metadata?: Record<string, unknown>; isActive?: boolean; description?: string }>;
  editingId?: string;
  saveError?: string | null;
  /** Valid master codes for ref-field validation (e.g. addon_groups for food_addons). */
  validRefCodes?: Partial<Record<string, string[]>>;
}

function isZoneField(field: MasterFieldDef) {
  return field.type === 'zone' || field.key === 'zoneCode';
}

function isMasterField(field: MasterFieldDef, masterTypeKey: string) {
  return field.type === 'master' || !!getMasterTypeForField(field, masterTypeKey);
}

function isEntityField(field: MasterFieldDef) {
  return field.type === 'entity' || field.entitySource === 'restaurants';
}

function imageConstraintsForField(field: MasterFieldDef, masterTypeKey: string, metadata: Record<string, string | number | boolean>) {
  return resolveMasterImageConstraints(field, masterTypeKey, metadata);
}

function MetadataFields({ fields, values, onChange, onPatchValues, masterTypeKey, errors, showErrors, liveMetaKeys = [], existingRecords = [], editingId, foodCategoryCode }: {
  fields: MasterFieldDef[];
  values: Record<string, string | number | boolean>;
  onChange: (key: string, value: string | number | boolean) => void;
  onPatchValues?: (patch: Record<string, string | number | boolean>) => void;
  masterTypeKey: string;
  errors: FieldErrors;
  showErrors: boolean;
  liveMetaKeys?: string[];
  existingRecords?: Array<{ _id?: string; metadata?: Record<string, unknown> }>;
  editingId?: string;
  foodCategoryCode?: string;
}) {
  const { t } = useI18n();
  /** Raw text of the focused currency input — formatting to 2 decimals on every keystroke blocks typing. */
  const [currencyDrafts, setCurrencyDrafts] = useState<Record<string, string>>({});
  if (!fields.length) return null;

  const labelOf = (text?: string) => (text ? translateUiText(text, t) : text);

  const sections = useMemo(() => {
    const map = new Map<string, MasterFieldDef[]>();
    fields.forEach((f) => {
      const sec = f.section || 'Details';
      if (!map.has(sec)) map.set(sec, []);
      map.get(sec)!.push(f);
    });
    return Array.from(map.entries());
  }, [fields]);

  const renderField = (field: MasterFieldDef) => {
    const errKey = `meta:${field.key}`;
    const fieldError = (showErrors || liveMetaKeys.includes(field.key)) ? errors[errKey] : undefined;
    const rawLabel = labelOf(field.label) || field.label;
    const label = isCurrencyField(field)
      ? chargeFieldLabel(stripCurrencyLabelSuffix(rawLabel))
      : rawLabel;
    const helper = labelOf(field.helperText) || field.helperText;
    const placeholder = labelOf(field.placeholder) || field.placeholder;
    const isEmptyRequired = field.required
      && liveMetaKeys.includes(field.key)
      && String(values[field.key] ?? '').trim() === '';
    const requiredHint = isEmptyRequired ? `${label} is required` : undefined;
    const showFieldError = !!fieldError || !!requiredHint;
    const fieldHelper = fieldError || requiredHint || helper;

    if (field.type === 'boolean') {
      return (
        <FormControlLabel
          control={(
            <Switch
              checked={!!values[field.key]}
              onChange={(e) => onChange(field.key, e.target.checked)}
            />
          )}
          label={label}
        />
      );
    }

    const isOpeningHourTimeField = masterTypeKey === 'restaurant_opening_hours'
      && (field.key === 'openTime' || field.key === 'closeTime');
    if (isOpeningHourTimeField && isOpeningHourClosed(values)) {
      return null;
    }

    if (
      masterTypeKey === 'delivery_incentives'
      && (field.key === 'startTime' || field.key === 'endTime' || field.key === 'start' || field.key === 'end')
      && !isDeliveryIncentivePeakHour(values.incentiveType)
    ) {
      return null;
    }

    if (isEntityField(field) && field.entitySource === 'restaurants') {
      return (
        <RestaurantSlugSelect
          label={label}
          value={String(values[field.key] ?? '')}
          onChange={(slug) => onChange(field.key, slug)}
          required={field.required}
          helperText={fieldHelper}
          error={showFieldError}
          errorText={fieldError || requiredHint}
        />
      );
    }

    if (isZoneField(field)) {
      return (
        <ZoneMasterSelect
          label={label}
          value={String(values[field.key] ?? '')}
          onChange={(code) => onChange(field.key, code)}
          required={field.required}
          helperText={helper}
        />
      );
    }

    if (masterTypeKey === 'food_units' && field.key === 'unitType') {
      const fromRecords = existingRecords
        .map((row) => normalizeFoodUnitType(row.metadata?.unitType))
        .filter(Boolean);
      const unitTypeOptions = [...new Set([
        ...FOOD_UNIT_TYPE_SUGGESTIONS,
        ...fromRecords,
        ...(field.options || []).map((option) => normalizeFoodUnitType(option.value)),
      ])].filter(Boolean);
      const currentValue = String(values[field.key] ?? '');
      return (
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          options={unitTypeOptions}
          getOptionLabel={(option) => formatFoodUnitTypeLabel(option) || option}
          value={currentValue}
          onChange={(_, nextValue) => onChange(field.key, normalizeFoodUnitType(nextValue || ''))}
          onInputChange={(_, nextValue, reason) => {
            if (reason === 'input') onChange(field.key, normalizeFoodUnitType(nextValue));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required={field.required}
              placeholder={placeholder || 'e.g. count, weight, volume'}
              helperText={fieldError || requiredHint || helper || 'Pick a common type or enter your own — e.g. count, weight, volume, portion'}
              error={showFieldError}
            />
          )}
        />
      );
    }

    if (masterTypeKey === 'kitchen_stations' && field.key === 'stationType') {
      const fromRecords = existingRecords
        .map((row) => normalizeKitchenStationType(row.metadata?.stationType))
        .filter(Boolean);
      const stationTypeOptions = [...new Set([
        ...KITCHEN_STATION_TYPE_SUGGESTIONS,
        ...fromRecords,
        ...(field.options || []).map((option) => normalizeKitchenStationType(option.value)),
      ])].filter(Boolean);
      const currentValue = String(values[field.key] ?? '');
      return (
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          options={stationTypeOptions}
          getOptionLabel={(option) => formatKitchenStationTypeLabel(option) || option}
          value={currentValue}
          onChange={(_, nextValue) => onChange(field.key, normalizeKitchenStationType(nextValue || ''))}
          onInputChange={(_, nextValue, reason) => {
            if (reason === 'input') onChange(field.key, normalizeKitchenStationType(nextValue));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required={field.required}
              placeholder={placeholder || 'e.g. hot_kitchen, bakery, grill'}
              helperText={fieldError || requiredHint || helper || 'Pick a common type or enter your own — e.g. Hot Kitchen, Bakery, Grill'}
              error={showFieldError}
            />
          )}
        />
      );
    }

    if (isMasterField(field, masterTypeKey)) {
      const refMasterType = getMasterTypeForField(field, masterTypeKey)!;
      if (field.multiple) {
        return (
          <SearchableMasterMultiSelect
            label={label}
            masterType={refMasterType}
            value={String(values[field.key] ?? '')}
            onChange={(codes) => onChange(field.key, codes)}
            required={field.required}
            helperText={helper}
            error={!!fieldError}
            errorText={fieldError}
          />
        );
      }
      return (
        <SearchableMasterSelect
          label={label}
          masterType={refMasterType}
          value={String(values[field.key] ?? '')}
          onChange={(code) => onChange(field.key, code)}
          required={field.required}
          helperText={helper}
          error={!!fieldError}
          errorText={fieldError}
          optionLabelMode={
            PARENT_CATEGORY_MASTER_TYPES.has(refMasterType) || field.key === 'parentCategoryCode'
              ? 'name'
              : 'name-code'
          }
        />
      );
    }

    if (field.type === 'select' && field.multiple) {
      const selected = field.key === 'daysOfWeek'
        ? parseCommaSeparatedDayValues(values[field.key])
        : String(values[field.key] ?? '')
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean);
      return (
        <TextField
          select
          fullWidth
          size="small"
          label={label}
          value={selected}
          onChange={(e) => {
            const next = e.target.value;
            const codes = typeof next === 'string' ? next.split(',') : next;
            const normalized = field.key === 'daysOfWeek'
              ? formatCommaSeparatedDayValues(codes as string[])
              : (codes as string[]).join(',');
            onChange(field.key, normalized);
          }}
          helperText={fieldError || helper}
          required={field.required}
          error={!!fieldError}
          SelectProps={{
            multiple: true,
            renderValue: (chosen) => {
              const valuesSelected = chosen as string[];
              if (!valuesSelected.length) return translateUiText('— Select —', t);
              return valuesSelected
                .map((value) => field.options?.find((option) => option.value === value)?.label || value)
                .join(', ');
            },
          }}
        >
          {(field.options || []).map((o) => (
            <MenuItem key={o.value} value={o.value}>{labelOf(o.label) || o.label}</MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.type === 'select') {
      const restaurantSlug = String(values.restaurantSlug ?? '');
      const mainBranchTaken = masterTypeKey === 'restaurant_branches'
        && field.key === 'branchType'
        && restaurantHasMainBranch(restaurantSlug, existingRecords, editingId);
      const openingDayTaken = (optionValue: string) => (
        masterTypeKey === 'restaurant_opening_hours'
        && field.key === 'day'
        && openingHourDayTaken(optionValue, existingRecords, editingId)
      );
      const branchHelper = field.key === 'branchType' && masterTypeKey === 'restaurant_branches'
        ? (mainBranchTaken
          ? 'This restaurant already has a Main branch — choose Sub Branch.'
          : 'Only one Main branch is allowed per restaurant.')
        : field.key === 'day' && masterTypeKey === 'restaurant_opening_hours'
          ? (isOpeningHourClosed(values)
            ? 'Select which weekday is closed all day — opening and closing times are not required.'
            : 'One opening-hours record per day — days already configured are disabled.')
          : helper;
      return (
        <TextField
          select fullWidth size="small" label={label}
          value={values[field.key] ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          helperText={fieldError || requiredHint || branchHelper}
          required={field.required}
          error={showFieldError}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (chosen) => {
              const selected = String(chosen ?? '');
              if (!selected) {
                return (
                  <Typography component="span" color="text.secondary">
                    {translateUiText('— Select —', t)}
                  </Typography>
                );
              }
              return labelOf(field.options?.find((option) => String(option.value) === selected)?.label || selected) || selected;
            },
          }}
        >
          {!field.required && (
            <MenuItem value="">{translateUiText('— Select —', t)}</MenuItem>
          )}
          {field.required && !String(values[field.key] ?? '').trim() && (
            <MenuItem value="" disabled>
              {translateUiText('— Select —', t)}
            </MenuItem>
          )}
          {(field.options || []).map((o) => (
            <MenuItem
              key={o.value}
              value={o.value}
              disabled={
                (field.key === 'branchType' && o.value === 'main' && mainBranchTaken)
                || openingDayTaken(String(o.value))
              }
            >
              {labelOf(o.label) || o.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.key === 'phone') {
      if (masterTypeKey === 'suppliers') {
        return (
          <PhoneWithDialCodeField
            label={label || 'Phone'}
            phone={String(values.phone ?? '')}
            countryCode={String(values.phoneCountryCode ?? '+91')}
            preferredCountry="IN"
            layout="inline"
            required={field.required}
            error={showFieldError}
            helperText={fieldHelper || 'Select country code, then enter the local phone number'}
            onChange={({ phone, countryCode }) => {
              if (onPatchValues) {
                onPatchValues({ phone, phoneCountryCode: countryCode });
              } else {
                onChange('phoneCountryCode', countryCode);
                onChange('phone', phone);
              }
            }}
          />
        );
      }
      return (
        <PhoneNumberField
          fullWidth
          size="small"
          label={label}
          value={String(values[field.key] ?? '')}
          onChange={(e) => onChange(field.key, e.target.value)}
          helperText={fieldHelper || 'Include country code (e.g. +91). 8–15 digits only.'}
          required={field.required}
          error={showFieldError}
          placeholder="+919876543210"
        />
      );
    }

    if (isAssetUrlField(field)) {
      const sampleImageUrl = masterTypeKey === 'food_categories' && field.key === 'imageUrl'
        ? resolveFoodCategorySampleImageUrl(foodCategoryCode || '')
        : masterTypeKey === 'cuisine_types' && field.key === 'imageUrl'
          ? resolveCuisineSampleImageUrl(foodCategoryCode || '')
          : masterTypeKey === 'food_addons' && field.key === 'imageUrl'
            ? resolveFoodAddonSampleImageUrl(foodCategoryCode || '')
            : masterTypeKey === 'packaging_types' && field.key === 'imageUrl'
              ? resolvePackagingSampleImageUrl(foodCategoryCode || '')
              : masterTypeKey === 'grocery_categories' && field.key === 'imageUrl'
                ? resolveGroceryCategorySampleImageUrl(foodCategoryCode || '')
              : masterTypeKey === 'grocery_sub_categories' && field.key === 'imageUrl'
                ? resolveGrocerySubCategorySampleImageUrl(foodCategoryCode || '')
              : masterTypeKey === 'grocery_product_images' && field.key === 'imageUrl'
                ? resolveGroceryProductSampleImageUrl(foodCategoryCode || '', values as Record<string, unknown>)
              : masterTypeKey === 'dietary_types' && field.key === 'icon'
                ? resolveDietarySampleImageUrl(foodCategoryCode || '')
                : undefined;
      const previewUrl = masterTypeKey === 'dietary_types' && field.key === 'icon'
        ? resolveDietaryIconUrl(foodCategoryCode || '', String(values[field.key] ?? ''))
        : masterTypeKey === 'food_addons' && field.key === 'imageUrl'
          ? resolveFoodAddonImagePreviewUrl(foodCategoryCode || '', values as Record<string, unknown>)
          : undefined;
      return (
        <FormFileUpload
          label={label}
          value={String(values[field.key] ?? '')}
          onChange={(url) => onChange(field.key, url)}
          folder={`masters/${masterTypeKey}/${field.key}`}
          helperText={helper}
          required={field.required}
          error={showFieldError}
          errorText={fieldError || requiredHint}
          sampleImageUrl={sampleImageUrl}
          previewUrl={previewUrl}
          imageConstraints={imageConstraintsForField(field, masterTypeKey, values)}
        />
      );
    }

    if (field.type === 'time' || isOpeningHourTimeField) {
      return (
        <TextField
          fullWidth
          size="small"
          label={label}
          type="time"
          value={String(values[field.key] ?? '')}
          onChange={(e) => {
            const parsed = sanitizeOpeningHourTimeInput(e.target.value);
            if (parsed === null) return;
            onChange(field.key, parsed);
          }}
          required={field.required}
          placeholder={placeholder || '06:00'}
          helperText={fieldError || helper || 'Use 24-hour format HH:mm (e.g. 06:00, 22:30)'}
          error={!!fieldError}
          inputProps={{ step: 60 }}
        />
      );
    }

    const isTagColor = isRideTagColorField(masterTypeKey, field.key);
    const isGenericColor = field.type === 'color' || field.key === 'color';

    if (isTagColor || isGenericColor) {
      const hex = String(values[field.key] ?? '');
      const pickerValue = RIDE_TAG_COLOR_PATTERN.test(hex) ? hex : '#0f766e';
      return (
        <Box>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {label}{field.required ? ' *' : ''}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
            <Box
              component="input"
              type="color"
              value={pickerValue}
              onChange={(e) => onChange(field.key, e.target.value)}
              aria-label={`${label} picker`}
              sx={{
                width: 48,
                height: 48,
                p: 0,
                border: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor: 'transparent',
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Hex code"
              value={hex || pickerValue}
              onChange={(e) => {
                const parsed = sanitizeRideTagColorInput(e.target.value);
                if (parsed === null) return;
                onChange(field.key, parsed);
              }}
              required={field.required}
              placeholder={placeholder || '#0f766e'}
              helperText={fieldError || helper || 'Use hex format — e.g. #0f766e'}
              error={showFieldError}
            />
          </Stack>
        </Box>
      );
    }

    const isCurrency = isCurrencyField(field);
    const isNumber = field.type === 'number' || field.type === 'percent' || isCurrency;
    const isPassengerCapacity = isVehiclePassengerCapacityField(masterTypeKey, field.key);
    const isRideTagColor = isRideTagColorField(masterTypeKey, field.key);

    return (
      <TextField
        fullWidth size="small" label={label}
        type={(isPassengerCapacity || field.key === 'capacity') ? 'text' : isCurrency ? 'text' : isNumber ? 'number' : 'text'}
        value={isCurrency
          ? (currencyDrafts[field.key] ?? formatCurrencyInputValue(values[field.key]))
          : (values[field.key] ?? '')}
        onFocus={isCurrency ? () => {
          const current = values[field.key];
          setCurrencyDrafts((prev) => ({
            ...prev,
            [field.key]: current === '' || current == null ? '' : String(current),
          }));
        } : undefined}
        onBlur={isCurrency ? () => {
          setCurrencyDrafts((prev) => {
            const next = { ...prev };
            delete next[field.key];
            return next;
          });
          const current = values[field.key];
          if (typeof current === 'string' && current.endsWith('.')) {
            const trimmed = current.slice(0, -1);
            onChange(field.key, trimmed === '' ? '' : Number(trimmed));
          }
        } : undefined}
        onChange={(e) => {
          const raw = e.target.value;
          if (isRideTagColor) {
            const parsed = sanitizeRideTagColorInput(raw);
            if (parsed === null) return;
            onChange(field.key, parsed);
            return;
          }
          if (isPassengerCapacity || field.key === 'capacity') {
            const parsed = sanitizePassengerCapacityInput(raw);
            if (parsed === null) return;
            onChange(field.key, parsed);
            return;
          }
          if (isCurrency) {
            const normalized = sanitizeCurrencyInput(raw).replace(/^0+(?=\d)/, '');
            if (normalized !== '' && !/^\d*(\.\d{0,2})?$/.test(normalized)) return;
            setCurrencyDrafts((prev) => ({ ...prev, [field.key]: normalized }));
            if (normalized === '') {
              onChange(field.key, '');
              return;
            }
            onChange(field.key, normalized.endsWith('.') ? normalized : Number(normalized));
            return;
          }
          if (isNumber) {
            const allowDecimals = field.key === 'baseKm'
              || field.key === 'maximumDeliveryKm'
              || (typeof field.min === 'number' && !Number.isInteger(field.min));
            if (allowDecimals) {
              if (/[-+,eE]/.test(raw)) return;
              if (raw !== '' && !/^\d*(\.\d{0,2})?$/.test(raw)) return;
              if (raw === '' || raw.endsWith('.')) {
                onChange(field.key, raw === '' ? '' : raw);
                return;
              }
              const next = Number(raw);
              if (!Number.isFinite(next)) return;
              if (field.min !== undefined && next < field.min) return;
              if (field.max !== undefined && next > field.max) return;
              onChange(field.key, next);
              return;
            }
            if (/[-+,eE.]/.test(raw)) return;
            if (raw !== '' && Number.isNaN(Number(raw))) return;
            const next = raw === '' ? '' : Number(raw);
            if (next !== '' && !Number.isInteger(next)) return;
            if (next !== '' && field.min !== undefined && next < field.min) return;
            if (next !== '' && field.max !== undefined && next > field.max) return;
            onChange(field.key, next);
            return;
          }
          onChange(field.key, raw);
        }}
        required={field.required || isPassengerCapacity || field.key === 'capacity'}
        placeholder={isCurrency ? (placeholder || '0.00') : placeholder}
        helperText={
          fieldError
          || requiredHint
          || (isCurrency
            ? (helper || 'Enter amount with up to 2 decimal places (e.g. 40.00)')
            : (isRideTagColor
              ? 'Use hex format — e.g. #f59e0b'
              : ((isPassengerCapacity || field.key === 'capacity')
                ? `Required — whole numbers from ${PASSENGER_CAPACITY_MIN} to ${PASSENGER_CAPACITY_MAX} seats`
                : helper)))
        }
        error={showFieldError}
        InputProps={isCurrency && getCurrencySymbol() ? {
          startAdornment: (
            <InputAdornment position="start">{getCurrencySymbol()}</InputAdornment>
          ),
        } : undefined}
        inputProps={{
          min: (isPassengerCapacity || field.key === 'capacity') ? PASSENGER_CAPACITY_MIN : field.min,
          max: (isPassengerCapacity || field.key === 'capacity') ? PASSENGER_CAPACITY_MAX : field.max,
          step: field.type === 'percent'
            ? 0.1
            : isCurrency
              ? 0.01
              : (field.key === 'baseKm' || field.key === 'maximumDeliveryKm')
                ? 0.01
                : (isPassengerCapacity || field.key === 'capacity') ? 1 : undefined,
          pattern: (isPassengerCapacity || field.key === 'capacity') ? '[0-9]*' : field.pattern,
          inputMode: isCurrency
            || field.key === 'baseKm'
            || field.key === 'maximumDeliveryKm'
            ? 'decimal'
            : (isPassengerCapacity || field.key === 'capacity') ? 'numeric' : undefined,
        }}
      />
    );
  };

  if (sections.length === 1 && sections[0][0] === 'Details') {
    return (
      <FormGrid>
        {fields.map((field) => (
          <Grid
            item
            xs={12}
            sm={field.type === 'boolean' || isAssetUrlField(field) || field.type === 'image' ? 12 : 6}
            key={field.key}
          >
            {renderField(field)}
          </Grid>
        ))}
      </FormGrid>
    );
  }

  return (
    <>
      {sections.map(([section, sectionFields]) => (
        <FormSection key={section} title={section} divider>
          <FormGrid>
            {sectionFields.map((field) => (
              <Grid
                item
                xs={12}
                sm={field.type === 'boolean' || isAssetUrlField(field) || field.type === 'image' ? 12 : 6}
                key={field.key}
              >
                {renderField(field)}
              </Grid>
            ))}
          </FormGrid>
        </FormSection>
      ))}
    </>
  );
}

export default function MasterFormDrawer({
  open,
  editing,
  schema,
  schemaLoading = false,
  currentType,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  parentMasterType,
  parentOptions = [],
  lockedSortOrder = null,
  existingRecords = [],
  editingId,
  saveError = null,
  validRefCodes,
}: Props) {
  const [showErrors, setShowErrors] = useState(false);
  const formContentRef = useRef<HTMLDivElement>(null);
  const [codeAuto, setCodeAuto] = useState(true);
  const commonManaged = COMMON_MANAGED_MASTER_TYPES.has(currentType);
  const supportsAutoCode = ON_DEMAND_AUTO_CODE_TYPES.has(currentType)
    || TRANSPORT_AUTO_CODE_TYPES.has(currentType)
    || FOOD_AUTO_CODE_TYPES.has(currentType)
    || DELIVERY_AUTO_CODE_TYPES.has(currentType)
    || commonManaged;
  const resolveAutoCode = (name: string) => (
    FOOD_AUTO_CODE_TYPES.has(currentType) || DELIVERY_AUTO_CODE_TYPES.has(currentType)
      ? slugifyFoodMasterCode(name)
      : commonManaged
        ? slugifyFoodMasterCode(name.replace(/%/g, ''))
        : slugifyMasterCode(name)
  );
  const prepTimeNameLocked = currentType === 'preparation_times';
  const taxGroupNameLocked = currentType === 'food_tax_groups' || currentType === 'grocery_tax_groups';
  const displayNameLocked = prepTimeNameLocked || taxGroupNameLocked;
  const autoServiceCodeField = supportsAutoCode
    ? schema?.metadataFields?.find((f) => f.key === 'serviceCode')
    : undefined;
  const letterOnlyName = TRANSPORT_LETTER_ONLY_NAME_TYPES.has(currentType)
    || FOOD_LETTER_ONLY_NAME_TYPES.has(currentType)
    || COMMON_LETTER_ONLY_NAME_TYPES.has(currentType);
  const validatedDescription = TRANSPORT_DESCRIPTION_VALIDATED_TYPES.has(currentType)
    || FOOD_DESCRIPTION_VALIDATED_TYPES.has(currentType)
    || DELIVERY_DESCRIPTION_VALIDATED_TYPES.has(currentType)
    || ON_DEMAND_DESCRIPTION_VALIDATED_TYPES.has(currentType)
    || commonManaged;
  const hideStatusToggle = commonManaged
    || TRANSPORT_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
    || FOOD_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
    || DELIVERY_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
    || ON_DEMAND_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
    || DELIVERY_STATUS_TOGGLE_LIST_TYPES.has(currentType)
    || currentType === 'grocery_store_types'
    || currentType === 'grocery_categories'
    || currentType === 'grocery_sub_categories'
    || currentType === 'grocery_brands'
    || currentType === 'warehouses'
    || currentType === 'product_variants'
    || currentType === 'weight_types'
    || currentType === 'suppliers'
    || currentType === 'stock_alerts'
    || currentType === 'grocery_tax_groups'
    || currentType === 'grocery_product_images'
    || currentType === 'grocery_availability_schedules'
    || currentType === 'delivery_partner_pricing'
    || currentType === 'delivery_slots'
    || currentType === 'packaging_types';
  /** Grocery / OnDemand masters: never show Active on Add — list toggle owns status. */
  const forceHideActiveStatus =
    currentType === 'grocery_categories'
    || currentType === 'grocery_store_types'
    || currentType === 'grocery_sub_categories'
    || currentType === 'grocery_brands'
    || currentType === 'warehouses'
    || currentType === 'product_variants'
    || currentType === 'weight_types'
    || currentType === 'suppliers'
    || currentType === 'stock_alerts'
    || currentType === 'grocery_tax_groups'
    || currentType === 'grocery_product_images'
    || currentType === 'grocery_availability_schedules'
    || currentType === 'delivery_partner_pricing'
    || currentType === 'delivery_slots'
    || currentType === 'packaging_types'
    || ON_DEMAND_HIDE_STATUS_TOGGLE_TYPES.has(currentType)
    || DELIVERY_STATUS_TOGGLE_LIST_TYPES.has(currentType)
    || commonManaged;
  const showFoodStatusSelect = FOOD_LIST_STATUS_TOGGLE_TYPES.has(currentType) && !hideStatusToggle && !forceHideActiveStatus;
  const showActiveSwitch = !hideStatusToggle && !showFoodStatusSelect && !forceHideActiveStatus;
  const hideDescription = TRANSPORT_HIDE_DESCRIPTION_TYPES.has(currentType);
  const useMetadataDescriptionOnly = FOOD_METADATA_DESCRIPTION_TYPES.has(currentType);
  const hideTopLevelDescription = hideDescription || useMetadataDescriptionOnly;
  const parentInBasicInfo = FOOD_PARENT_IN_BASIC_INFO_TYPES.has(currentType);
  const attributeParentInBasicInfo = currentType === 'food_attribute_values';
  const editingDeprecated = editing ? isMasterRecordDeprecated(editing) : false;
  const strictIntegerSortOrder = TRANSPORT_INTEGER_SORT_ORDER_TYPES.has(currentType)
    || FOOD_INTEGER_SORT_ORDER_TYPES.has(currentType)
    || DELIVERY_INTEGER_SORT_ORDER_TYPES.has(currentType)
    || ON_DEMAND_INTEGER_SORT_ORDER_TYPES.has(currentType)
    || commonManaged;
  const uniqueSortOrder = commonManaged
    || TRANSPORT_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || FOOD_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || DELIVERY_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || ON_DEMAND_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || FOOD_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || DELIVERY_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType);
  const parentScopedSortOrder = TRANSPORT_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || FOOD_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || DELIVERY_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType)
    || ON_DEMAND_PARENT_SCOPED_UNIQUE_SORT_ORDER_TYPES.has(currentType);
  const hasParent = !!(schema?.supportsHierarchy && parentMasterType);
  const parentOptional = hasParent && OPTIONAL_PARENT_TYPES.has(currentType);
  const parentRequired = hasParent && !parentOptional;
  const hasGeoFields = (schema?.geoFields?.length || 0) > 0;
  const metaFieldsRaw = (schema?.metadataFields || []).filter((f) =>
    // sortOrder / status live on the record — avoid duplicate metadata fields
    f.key !== 'sortOrder' && f.key !== 'status'
    // Top-level Description in Basic Info — skip duplicate metadata description field
    && (hideDescription || useMetadataDescriptionOnly || f.key !== 'description')
    && !(
      (currentType === 'grocery_categories'
        || currentType === 'grocery_store_types'
        || currentType === 'grocery_brands'
        || currentType === 'warehouses'
        || currentType === 'product_variants'
        || currentType === 'weight_types'
        || currentType === 'suppliers'
        || currentType === 'stock_alerts'
        || currentType === 'grocery_tax_groups'
        || currentType === 'grocery_product_images'
        || currentType === 'grocery_availability_schedules')
      && (f.key === 'description' || f.key === 'status' || f.key === 'sortOrder')
    )
    && (!isTransportHubType(currentType) || (f.key !== 'centerLat' && f.key !== 'centerLng'))
    && !(autoServiceCodeField && f.key === 'serviceCode')
    && !(parentInBasicInfo && f.key === 'parentCategoryCode')
    && !(currentType === 'food_attribute_values' && f.key === 'attributeCode')
    && !(currentType === 'food_variants' && (f.key === 'foodCode' || f.key === 'status'))
  );
  const metaFields = useMemo(() => {
    if (currentType === 'restaurant_opening_hours') {
      const order = ['day', 'isClosed', 'openTime', 'closeTime'];
      return [...metaFieldsRaw].sort((a, b) => {
        const ai = order.indexOf(a.key);
        const bi = order.indexOf(b.key);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
    }
    if (currentType === 'food_categories') {
      const order = ['imageUrl'];
      return [...metaFieldsRaw].sort((a, b) => {
        const ai = order.indexOf(a.key);
        const bi = order.indexOf(b.key);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
    }
    if (currentType === 'grocery_product_images') {
      const order = ['skuCode', 'imageType', 'imageUrl'];
      const constraints = groceryProductImageConstraintsForType(form.metadata.imageType);
      return [...metaFieldsRaw]
        .map((field) => {
          if (field.key === 'imageUrl' || field.type === 'image') {
            return {
              ...field,
              type: 'image' as const,
              required: true,
              imageMaxWidth: constraints.imageMaxWidth,
              imageMaxHeight: constraints.imageMaxHeight,
              imageAspectRatio: constraints.imageAspectRatio,
              helperText: constraints.helperText,
            };
          }
          return field;
        })
        .sort((a, b) => {
          const ai = order.indexOf(a.key);
          const bi = order.indexOf(b.key);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });
    }
    return metaFieldsRaw;
  }, [metaFieldsRaw, currentType, form.metadata.imageType]);
  const openingHourDayOptions = useMemo(
    () => schema?.metadataFields?.find((f) => f.key === 'day')?.options || [],
    [schema?.metadataFields],
  );
  const allOpeningDaysTaken = useMemo(
    () => currentType === 'restaurant_opening_hours'
      && !editing
      && allOpeningHourDaysTaken(openingHourDayOptions, existingRecords, editingId),
    [currentType, editing, openingHourDayOptions, existingRecords, editingId],
  );
  const hideOpeningHourSortOrder = currentType === 'restaurant_opening_hours' && !editing;
  /** Hide auto sort on create for most delivery masters; keep visible (read-only) for tiers/docs/cancellation/substitution/store types. */
  const hideAutoSortOrderOnCreate = !editing
    && (
      (FOOD_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
        && currentType !== 'food_sub_categories'
        && currentType !== 'food_attribute_values')
      || (DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
        && currentType !== 'delivery_partners_tiers'
        && currentType !== 'merchant_document_types'
        && currentType !== 'cancellation_reasons'
        && currentType !== 'substitution_rules'
        && currentType !== 'delivery_slots'
        && currentType !== 'packaging_types'
        && currentType !== 'delivery_partner_pricing'
        && currentType !== 'grocery_store_types'
        && currentType !== 'grocery_categories'
        && currentType !== 'grocery_brands'
        && currentType !== 'warehouses'
        && currentType !== 'product_variants'
        && currentType !== 'weight_types'
        && currentType !== 'suppliers'
        && currentType !== 'stock_alerts'
        && currentType !== 'grocery_tax_groups'
        && currentType !== 'grocery_product_images'
        && currentType !== 'grocery_availability_schedules'
        && currentType !== 'grocery_sub_categories')
    );
  const promoteImageToBasicInfo = FOOD_IMAGE_IN_BASIC_INFO_TYPES.has(currentType);
  const metaImageFields = useMemo(() => {
    const fromSchema = metaFields.filter((f) => isAssetUrlField(f) || f.type === 'image');
    if (fromSchema.length > 0) return fromSchema;
    if (currentType === 'food_categories') {
      return [{
        key: 'imageUrl',
        label: 'Category Image',
        type: 'image' as const,
        required: true,
      }];
    }
    return fromSchema;
  }, [metaFields, currentType]);
  const propertiesMetaFields = useMemo(
    () => (promoteImageToBasicInfo
      ? metaFields.filter((f) => !isAssetUrlField(f) && f.type !== 'image')
      : metaFields),
    [metaFields, promoteImageToBasicInfo],
  );
  const hasBasicInfoImage = promoteImageToBasicInfo && metaImageFields.length > 0;
  const allValidatedMetaFields = useMemo(() => {
    const seen = new Set<string>();
    const merged: MasterFieldDef[] = [];
    for (const field of [...metaImageFields, ...metaFields]) {
      if (seen.has(field.key)) continue;
      seen.add(field.key);
      merged.push(field);
    }
    return merged;
  }, [metaFields, metaImageFields]);
  const showParentMasterSection = hasParent
    && !FOOD_HIDE_PARENT_PICKER_TYPES.has(currentType)
    && !parentInBasicInfo;
  const parentCategoryField = useMemo(
    () => schema?.metadataFields?.find((f) => f.key === 'parentCategoryCode') ?? null,
    [schema?.metadataFields],
  );
  const isFoodMaster = currentType.startsWith('food_') || currentType.startsWith('restaurant_') || ['cuisine_types', 'addon_groups', 'kitchen_stations', 'preparation_times'].includes(currentType);
  const isDeliveryGroceryMaster = currentType === 'grocery_categories'
    || currentType === 'grocery_store_types'
    || currentType === 'grocery_sub_categories'
    || currentType === 'grocery_brands'
    || currentType === 'warehouses'
    || currentType === 'product_variants'
    || currentType === 'weight_types'
    || currentType === 'suppliers'
    || currentType === 'stock_alerts'
    || currentType === 'grocery_tax_groups'
    || currentType === 'grocery_product_images'
    || currentType === 'grocery_availability_schedules';
  const isOnDemandMaster = ON_DEMAND_AUTO_CODE_TYPES.has(currentType);
  const drawerWidth = isFoodMaster || isOnDemandMaster || isDeliveryGroceryMaster || currentType === 'service_categories' ? 640 : 520;

  useEffect(() => {
    if (open) {
      setShowErrors(false);
      setCodeAuto(!editing && supportsAutoCode);
    }
  }, [open, currentType, editing, supportsAutoCode]);

  const sortOrderAttributeCode = useMemo(() => {
    if (!parentScopedSortOrder || currentType === 'food_sub_categories') return undefined;
    const fromMeta = String(form.metadata.attributeCode ?? '').trim();
    if (fromMeta) return fromMeta;
    const selected = parentOptions.find((p) => p._id === form.parentId);
    if (selected?.code) return selected.code;
    return undefined;
  }, [parentScopedSortOrder, currentType, parentOptions, form.parentId, form.metadata.attributeCode]);

  const sortOrderParentCategoryCode = useMemo(() => {
    if (currentType !== 'food_sub_categories' && currentType !== 'grocery_sub_categories') return undefined;
    const fromMeta = String(form.metadata.parentCategoryCode ?? '').trim();
    if (fromMeta) return fromMeta;
    const selected = parentOptions.find((p) => p._id === form.parentId);
    return selected?.code;
  }, [currentType, form.metadata.parentCategoryCode, form.parentId, parentOptions]);

  const sortOrderScopeRecords = useMemo(
    () => filterRecordsForParentScopedSortOrder(currentType, existingRecords, {
      parentId: form.parentId,
      attributeCode: sortOrderAttributeCode,
      parentCategoryCode: sortOrderParentCategoryCode,
    }),
    [currentType, existingRecords, form.parentId, sortOrderAttributeCode, sortOrderParentCategoryCode],
  );

  const effectiveLockedSortOrder = useMemo(() => {
    if (editing) return lockedSortOrder;
    if (currentType === 'food_sub_categories') {
      if (!sortOrderParentCategoryCode) return null;
      return getNextFoodSubCategorySortOrder(existingRecords, sortOrderParentCategoryCode);
    }
    if (currentType === 'grocery_sub_categories') {
      if (!sortOrderParentCategoryCode) return null;
      return getNextGrocerySubCategorySortOrder(existingRecords, sortOrderParentCategoryCode);
    }
    if (currentType === 'food_attribute_values') {
      if (!sortOrderAttributeCode) return null;
      return getNextFoodAttributeValueSortOrder(existingRecords, sortOrderAttributeCode);
    }
    if (
      currentType === 'grocery_categories'
      || currentType === 'grocery_store_types'
      || currentType === 'grocery_brands'
      || currentType === 'warehouses'
      || currentType === 'product_variants'
      || currentType === 'weight_types'
      || currentType === 'suppliers'
      || currentType === 'stock_alerts'
      || currentType === 'grocery_tax_groups'
      || currentType === 'grocery_product_images'
      || currentType === 'grocery_availability_schedules'
      || FOOD_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
      || DELIVERY_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
      || ON_DEMAND_SEQUENTIAL_SORT_ORDER_TYPES.has(currentType)
      || commonManaged
    ) {
      // Prefer parent-provided lock, but never leave create on 0 / null.
      const fromParent = lockedSortOrder != null && lockedSortOrder >= 1 ? lockedSortOrder : null;
      return fromParent ?? getNextFoodMasterSortOrder(existingRecords);
    }
    return lockedSortOrder;
  }, [editing, currentType, lockedSortOrder, sortOrderParentCategoryCode, sortOrderAttributeCode, existingRecords]);

  useEffect(() => {
    if (open && !editing && effectiveLockedSortOrder != null && form.sortOrder !== effectiveLockedSortOrder) {
      onChange({ ...form, sortOrder: effectiveLockedSortOrder });
    }
  }, [open, editing, effectiveLockedSortOrder, form, onChange]);

  const errors = useMemo(() => {
    const base = validateMasterForm(form, schema, {
      hasParent: parentRequired && !FOOD_HIDE_PARENT_PICKER_TYPES.has(currentType),
      requiredSortOrder: !editing && effectiveLockedSortOrder != null ? effectiveLockedSortOrder : undefined,
      existingRecords: sortOrderScopeRecords,
      editingId,
      masterType: currentType,
      sortOrderParentId: form.parentId,
      sortOrderAttributeCode,
      sortOrderParentCategoryCode,
      branchRecords: currentType === 'restaurant_branches' ? existingRecords : undefined,
      openingHourRecords: currentType === 'restaurant_opening_hours' ? existingRecords : undefined,
      validRefCodes,
    });
    if (currentType === 'vehicle_types') {
      const capacityErr = validatePassengerCapacity(
        form.metadata.capacity,
        'Passenger Capacity',
        { required: true },
      );
      if (capacityErr) base['meta:capacity'] = capacityErr;
      else delete base['meta:capacity'];

      const serviceTypesErr = validateVehicleServiceTypes(form.metadata.serviceTypes);
      if (serviceTypesErr) base['meta:serviceTypes'] = serviceTypesErr;
      else delete base['meta:serviceTypes'];
    }
    for (const field of metaImageFields) {
      if (field.required && !String(form.metadata[field.key] ?? '').trim()) {
        base[`meta:${field.key}`] = `${field.label || field.key} is required`;
      }
    }
    return base;
  }, [form, schema, parentRequired, editing, effectiveLockedSortOrder, sortOrderScopeRecords, editingId, currentType, sortOrderAttributeCode, sortOrderParentCategoryCode, existingRecords, metaImageFields]);

  const codeDuplicateError = useMemo(
    () => (currentType === 'food_sub_categories'
      ? validateDuplicateFoodSubCategoryCode(
        form.code,
        String(form.metadata.parentCategoryCode ?? ''),
        existingRecords,
        editingId,
      )
      : validateDuplicateMasterCode(form.code, existingRecords, editingId)),
    [currentType, form.code, form.metadata.parentCategoryCode, existingRecords, editingId],
  );

  const skillNameDuplicateError = useMemo(
    () => (currentType === 'skills'
      ? validateDuplicateSkillName(form.name, existingRecords, editingId)
      : undefined),
    [currentType, form.name, existingRecords, editingId],
  );

  const sortOrderDuplicateError = useMemo(
    () => (uniqueSortOrder
      ? validateDuplicateSortOrder(form.sortOrder, sortOrderScopeRecords, editingId)
      : undefined),
    [form.sortOrder, sortOrderScopeRecords, editingId, uniqueSortOrder],
  );

  const passengerCapacityError = useMemo(
    () => (currentType === 'vehicle_types'
      ? validatePassengerCapacity(form.metadata.capacity, 'Passenger Capacity', { required: true })
      : undefined),
    [currentType, form.metadata.capacity],
  );

  const vehicleServiceTypesError = useMemo(
    () => (currentType === 'vehicle_types'
      ? validateVehicleServiceTypes(form.metadata.serviceTypes)
      : undefined),
    [currentType, form.metadata.serviceTypes],
  );

  const tagColorError = useMemo(
    () => (isRideTagColorField(currentType, 'color')
      ? validateRideTagColor(form.metadata.color, 'Tag Color', { required: true })
      : undefined),
    [currentType, form.metadata.color],
  );

  const descriptionError = useMemo(
    () => {
      if (!validatedDescription) return undefined;
      if (currentType === 'delivery_partner_pricing') {
        return validateDeliveryPartnerPricingDescription(form.description);
      }
      return validateOptionalDescription(form.description);
    },
    [form.description, validatedDescription, currentType],
  );

  const sortOrderFieldDisabled = !editing && (
    effectiveLockedSortOrder != null
    || (currentType === 'food_sub_categories' && !sortOrderParentCategoryCode)
    || (currentType === 'grocery_sub_categories' && !sortOrderParentCategoryCode)
    || (currentType === 'food_attribute_values' && !sortOrderAttributeCode)
  );
  const sortOrderFieldValue = (() => {
    if (currentType === 'food_sub_categories' && !sortOrderParentCategoryCode && !editing) return '';
    if (currentType === 'grocery_sub_categories' && !sortOrderParentCategoryCode && !editing) return '';
    if (currentType === 'food_attribute_values' && !sortOrderAttributeCode && !editing) return '';
    if (!editing && effectiveLockedSortOrder != null) return effectiveLockedSortOrder;
    if (form.sortOrder >= 1) return form.sortOrder;
    return '';
  })();

  const invalidSortOrder = !editing && strictIntegerSortOrder
    && (!Number.isInteger(form.sortOrder) || form.sortOrder < 1)
    && !(hideOpeningHourSortOrder && !String(form.metadata.day ?? '').trim())
    && !hideAutoSortOrderOnCreate
    && !(currentType === 'food_sub_categories' && !sortOrderParentCategoryCode)
    && !(currentType === 'grocery_sub_categories' && !sortOrderParentCategoryCode)
    && !(currentType === 'food_attribute_values' && !sortOrderAttributeCode);

  const blockingMetaErrors = useMemo(
    () => allValidatedMetaFields.some((field) => {
      const errKey = `meta:${field.key}`;
      const value = form.metadata[field.key];
      const hasValue = String(value ?? '').trim() !== '';
      if (!field.required && !hasValue) return false;
      return !!errors[errKey];
    }),
    [allValidatedMetaFields, form.metadata, errors],
  );

  const requiredMetaLiveKeys = useMemo(
    () => (editing
      ? []
      : allValidatedMetaFields
        .filter((field) => field.required && !isAssetUrlField(field) && field.type !== 'image')
        .map((field) => field.key)),
    [editing, allValidatedMetaFields],
  );

  const errorSummary = useMemo(
    () => summarizeMasterFormErrors(errors, schema),
    [errors, schema],
  );

  const setField = (patch: Partial<MasterFormState>) => onChange({ ...form, ...patch });
  const setMeta = (key: string, value: string | number | boolean) => {
    const metadata = { ...form.metadata, [key]: value };
    const patch: Partial<MasterFormState> = { metadata };

    if (currentType === 'restaurant_opening_hours' && key === 'isClosed' && value === true) {
      metadata.openTime = '';
      metadata.closeTime = '';
    }

    if (currentType === 'restaurant_opening_hours' && key === 'day' && typeof value === 'string' && value) {
      patch.sortOrder = sortOrderForOpeningHourDay(value);
      if (!editing) {
        const dayName = `${formatOpeningHourDayLabel(value)} Hours`;
        patch.name = dayName;
        if (codeAuto && supportsAutoCode) {
          patch.code = resolveAutoCode(dayName);
        }
      }
    }

    const legacyKey = LEGACY_PARENT_META_KEY[currentType];
    if (legacyKey && key === legacyKey && typeof value === 'string' && value) {
      const parent = parentOptions.find((p) => p.code === value);
      if (parent) patch.parentId = parent._id;
      if (!editing && currentType === 'food_sub_categories') {
        patch.sortOrder = getNextFoodSubCategorySortOrder(existingRecords, value);
      }
      if (!editing && currentType === 'food_attribute_values') {
        patch.sortOrder = getNextFoodAttributeValueSortOrder(existingRecords, value);
      }
    }
    if (currentType === 'preparation_times' && key === 'minutes') {
      const minutes = Number(value);
      if (Number.isFinite(minutes) && minutes >= 1) {
        patch.name = formatPreparationTimeName(minutes);
        if (!editing && codeAuto) {
          patch.code = preparationTimeCodeFromMinutes(minutes);
        }
      }
    }
    if ((currentType === 'food_tax_groups' || currentType === 'grocery_tax_groups') && key === 'taxPercent') {
      const taxPercent = Number(value);
      if (Number.isFinite(taxPercent)) {
        patch.name = formatFoodTaxGroupName(taxPercent);
        if (!editing && codeAuto) {
          patch.code = resolveAutoCode(String(patch.name)) || form.code;
        }
      }
    }
    if (currentType === 'delivery_partner_pricing') {
      const pricingKeys = new Set([
        'zoneCode',
        'serviceSlug',
        'baseKm',
        'baseAmount',
        'remainingKmRate',
        'pickupCharge',
        'dropCharge',
        'minimumGuarantee',
        'maximumDeliveryKm',
      ]);
      if (pricingKeys.has(key)) {
        const suggestedName = formatDeliveryPartnerPricingName(metadata.zoneCode, metadata.serviceSlug);
        if (suggestedName && (!editing || isPlaceholderDeliveryPartnerPricingName(form.name))) {
          if (isPlaceholderDeliveryPartnerPricingName(form.name) || !String(form.name ?? '').trim()) {
            patch.name = suggestedName;
            if (!editing && codeAuto && supportsAutoCode) {
              patch.code = resolveAutoCode(suggestedName);
            }
          }
        }
        const suggestedDescription = formatDeliveryPartnerPricingDescription(metadata);
        if (
          suggestedDescription
          && isPlaceholderDeliveryPartnerPricingDescription(form.description)
        ) {
          patch.description = suggestedDescription;
        }
      }
    }
    if (currentType === 'delivery_incentives' && key === 'incentiveType') {
      if (isDeliveryIncentivePeakHour(value)) {
        if (!metadata.startTime) metadata.startTime = '11:00';
        if (!metadata.endTime) metadata.endTime = '14:00';
      } else {
        delete metadata.startTime;
        delete metadata.endTime;
      }
    }
    onChange({ ...form, ...patch });
  };

  const patchMeta = (patchValues: Record<string, string | number | boolean>) => {
    onChange({ ...form, metadata: { ...form.metadata, ...patchValues } });
  };

  const parentLabel = currentType === 'food_attribute_values'
    ? 'Parent Attribute'
    : currentType === 'service_categories'
      ? 'Parent category (primary)'
      : parentMasterType
        ? `Parent ${parentMasterType.replace(/_/g, ' ')}`
        : 'Parent master';

  const handleNameChange = (name: string) => {
    if (!editing && supportsAutoCode && codeAuto) {
      const code = resolveAutoCode(name);
      setField({
        name,
        code,
        metadata: { ...form.metadata, serviceCode: code },
      });
    } else {
      setField({ name });
    }
  };

  const regenerateCodesFromName = () => {
    if (currentType === 'preparation_times') {
      const minutes = Number(form.metadata.minutes);
      if (!Number.isFinite(minutes) || minutes < 1) return;
      setCodeAuto(true);
      setField({
        name: formatPreparationTimeName(minutes),
        code: preparationTimeCodeFromMinutes(minutes),
      });
      return;
    }
    const source = currentType === 'restaurant_opening_hours' && form.metadata.day
      ? `${formatOpeningHourDayLabel(form.metadata.day)} Hours`
      : form.name;
    let code = resolveAutoCode(source);
    if (!code && (FOOD_AUTO_CODE_TYPES.has(currentType) || DELIVERY_AUTO_CODE_TYPES.has(currentType))) {
      const prefix = currentType === 'cancellation_reasons' ? 'cancel' : 'code';
      code = `${prefix}_${Math.random().toString(36).slice(2, 8)}`;
    }
    if (!code) return;
    setCodeAuto(true);
    setField({
      ...(currentType === 'restaurant_opening_hours' && form.metadata.day && !form.name.trim()
        ? { name: source }
        : {}),
      code,
      metadata: { ...form.metadata, serviceCode: code },
    });
  };

  const requiredImageLiveKeys = useMemo(
    () => {
      if (editing) return [];
      const requiredImages = allValidatedMetaFields
        .filter((field) => field.required && (isAssetUrlField(field) || field.type === 'image'))
        .map((field) => field.key);
      if (showErrors) return requiredImages;
      return requiredImages.filter((key) => !String(form.metadata[key] ?? '').trim());
    },
    [editing, allValidatedMetaFields, form.metadata, showErrors],
  );

  const metadataLiveKeys = useMemo(
    () => [
      ...requiredMetaLiveKeys,
      ...requiredImageLiveKeys,
      ...(currentType === 'vehicle_types' ? ['capacity', 'serviceTypes'] : []),
      ...(isRideTagColorField(currentType, 'color') ? ['color'] : []),
      ...(currentType === 'food_sub_categories' ? ['parentCategoryCode'] : []),
      ...(currentType === 'food_attribute_values' ? ['attributeCode'] : []),
      ...(currentType === 'service_availability_schedules' || currentType === 'food_availability_schedules'
        ? ['startTime', 'endTime', 'daysOfWeek']
        : []),
      ...(currentType === 'delivery_slots' ? ['start', 'end', 'fee'] : []),
      ...(currentType === 'packaging_types' ? ['imageUrl', 'fee'] : []),
      ...(currentType === 'experience_levels'
        ? ['minYears', 'maxYears'].filter((k) => String(form.metadata[k] ?? '').trim() !== '')
        : []),
      ...(currentType === 'delivery_partner_pricing'
        ? [
          'zoneCode',
          'serviceSlug',
          'baseKm',
          'baseAmount',
          'remainingKmRate',
          'pickupCharge',
          'dropCharge',
          'waitingChargePerMinute',
          'minimumGuarantee',
          'maximumDeliveryKm',
        ]
        : []),
      ...(currentType === 'delivery_incentives' ? ['zoneCode', 'serviceSlug', 'incentiveType', 'amount', 'startTime', 'endTime'] : []),
      ...(currentType === 'delivery_partners_tiers' ? ['minOrders', 'bonusPercent'] : []),
      ...(currentType === 'merchant_document_types' ? ['category', 'entityTypes'] : []),
      ...(currentType === 'cancellation_reasons' ? ['appliesTo'] : []),
      ...(currentType === 'substitution_rules' ? ['allowSubstitute'] : []),
      ...(currentType === 'grocery_product_images' ? ['skuCode', 'imageType', 'imageUrl'] : []),
      ...(currentType === 'grocery_availability_schedules' ? ['day', 'startTime', 'endTime'] : []),
      ...(currentType === 'restaurant_opening_hours' ? ['day', 'isClosed', 'openTime', 'closeTime'] : []),
    ],
    [requiredMetaLiveKeys, requiredImageLiveKeys, currentType, form.metadata],
  );

  const scrollToFirstFormError = () => {
    requestAnimationFrame(() => {
      const root = formContentRef.current;
      if (!root) return;
      root.querySelector('[data-validation-summary]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const firstInvalid = root.querySelector('.Mui-error, [aria-invalid="true"]');
      firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handleSubmit = () => {
    setShowErrors(true);
    const next = { ...form, metadata: { ...form.metadata } };
    if (currentType === 'preparation_times') {
      const minutes = Number(next.metadata.minutes);
      if (Number.isFinite(minutes) && minutes >= 1) {
        next.name = formatPreparationTimeName(minutes);
        if (!editing) {
          next.code = preparationTimeCodeFromMinutes(minutes);
        }
      }
    }
    if (currentType === 'food_tax_groups' || currentType === 'grocery_tax_groups') {
      const taxPercent = Number(next.metadata.taxPercent);
      if (Number.isFinite(taxPercent)) {
        next.name = formatFoodTaxGroupName(taxPercent);
        if (!editing && supportsAutoCode && codeAuto) {
          const code = resolveAutoCode(next.name) || next.code;
          next.code = code;
          next.metadata.serviceCode = code;
        }
      }
    } else if (!editing && supportsAutoCode && codeAuto) {
      const code = resolveAutoCode(form.name) || form.code;
      next.code = code;
      next.metadata.serviceCode = code;
    }
    if (
      !editing
      && effectiveLockedSortOrder != null
      && (
        currentType === 'grocery_brands'
        || currentType === 'grocery_categories'
        || currentType === 'grocery_store_types'
        || currentType === 'warehouses'
        || currentType === 'product_variants'
        || currentType === 'weight_types'
        || currentType === 'suppliers'
        || currentType === 'stock_alerts'
        || currentType === 'grocery_tax_groups'
      )
    ) {
      next.sortOrder = effectiveLockedSortOrder;
    }
    if (
      next.code !== form.code
      || next.name !== form.name
      || next.sortOrder !== form.sortOrder
      || next.metadata.serviceCode !== form.metadata.serviceCode
    ) {
      onChange(next);
    }
    const nextErrors = validateMasterForm(next, schema, {
      hasParent: parentRequired && !FOOD_HIDE_PARENT_PICKER_TYPES.has(currentType),
      requiredSortOrder: !editing && effectiveLockedSortOrder != null ? effectiveLockedSortOrder : undefined,
      existingRecords: sortOrderScopeRecords,
      editingId,
      masterType: currentType,
      sortOrderParentId: form.parentId,
      sortOrderAttributeCode,
      sortOrderParentCategoryCode,
      branchRecords: currentType === 'restaurant_branches' ? existingRecords : undefined,
      openingHourRecords: currentType === 'restaurant_opening_hours' ? existingRecords : undefined,
      validRefCodes,
    });
    for (const field of metaImageFields) {
      if (field.required && !String(next.metadata[field.key] ?? '').trim()) {
        nextErrors[`meta:${field.key}`] = `${field.label || field.key} is required`;
      }
    }
    if (uniqueSortOrder) {
      const dupSort = validateDuplicateSortOrder(next.sortOrder, sortOrderScopeRecords, editingId);
      if (dupSort) {
        if (
          !editing
          && (
            currentType === 'grocery_brands'
            || currentType === 'grocery_categories'
            || currentType === 'grocery_store_types'
            || currentType === 'warehouses'
            || currentType === 'product_variants'
            || currentType === 'weight_types'
            || currentType === 'suppliers'
            || currentType === 'stock_alerts'
            || currentType === 'grocery_tax_groups'
            || currentType === 'grocery_product_images'
            || currentType === 'grocery_availability_schedules'
            || currentType === 'delivery_slots'
            || currentType === 'packaging_types'
            || currentType === 'delivery_partner_pricing'
          )
        ) {
          next.sortOrder = getNextFoodMasterSortOrder(existingRecords);
          onChange(next);
          const stillDup = validateDuplicateSortOrder(next.sortOrder, sortOrderScopeRecords, editingId);
          if (stillDup) nextErrors.sortOrder = stillDup;
          else delete nextErrors.sortOrder;
        } else {
          nextErrors.sortOrder = dupSort;
        }
      }
    }
    if (!isMasterFormValid(nextErrors)) {
      scrollToFirstFormError();
      return;
    }
    if (editingDeprecated) {
      next.isActive = false;
      if (schema?.metadataFields?.some((f) => f.key === 'status')) {
        next.metadata = { ...next.metadata, status: 'inactive' };
      }
      onChange(next);
    }
    onSubmit();
  };

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} ${schema?.label || currentType.replace(/_/g, ' ')}`}
      subtitle={schema?.description || 'Fill in the details below.'}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={editing ? 'Update' : 'Create'}
      loading={loading}
      disableSubmit={
        schemaLoading
        || (commonManaged && (!isMasterFormValid(errors) || !!sortOrderDuplicateError))
        || !!codeDuplicateError
        || !!skillNameDuplicateError
        || !!descriptionError
        || !!passengerCapacityError
        || !!vehicleServiceTypesError
        || !!tagColorError
        || invalidSortOrder
        || blockingMetaErrors
        || allOpeningDaysTaken
      }
      width={drawerWidth}
      contentRef={formContentRef}
    >
      {allOpeningDaysTaken && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          All weekdays (Monday–Sunday) already have opening-hours records. To mark a day as closed all day,
          edit the existing record for that day instead of creating a new one.
        </Alert>
      )}
      {schemaLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>Loading form fields…</Alert>
      )}
      {editingDeprecated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This record is deprecated and cannot be reactivated. You may update metadata or delete it.
        </Alert>
      )}
      {showErrors && !isMasterFormValid(errors) && (
        <Alert severity="error" sx={{ mb: 2 }} data-validation-summary>
          Please fix the highlighted fields before saving.
          {errorSummary.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              {errorSummary.map((line) => (
                <Box component="li" key={line} sx={{ typography: 'body2' }}>{line}</Box>
              ))}
            </Box>
          )}
        </Alert>
      )}
      {commonManaged && !showErrors && !isMasterFormValid(errors) && errorSummary.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {editing ? 'Update' : 'Create'} is enabled once these are fixed:
          <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2.5 }}>
            {errorSummary.map((line) => (
              <Box component="li" key={line} sx={{ typography: 'body2' }}>{line}</Box>
            ))}
          </Box>
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={undefined}>
          {saveError}
        </Alert>
      )}

      <FormSection title="Basic info" divider>
        <FormGrid>
          <Grid item xs={12} sm={6}>
            {letterOnlyName ? (
              <PersonNameField
                required
                label="Display name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                helperText={
                  (showErrors || !!form.name) && errors.name
                    ? errors.name
                    : 'Letters only — no numbers or special characters'
                }
                error={(showErrors || !!form.name) && !!errors.name}
                autoFocus={!editing}
                inputProps={{ maxLength: 50 }}
              />
            ) : (
              <TextField
                required
                label="Display name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                helperText={
                  skillNameDuplicateError
                    || (showErrors && errors.name
                      ? errors.name
                      : prepTimeNameLocked
                        ? 'Auto-filled from minutes — e.g. 15 mins'
                        : taxGroupNameLocked
                          ? 'Auto-filled from Tax % — e.g. GST 5%'
                        : currentType === 'delivery_partner_pricing'
                          ? 'Use a meaningful name — e.g. WORLD Food Delivery Partner Pricing'
                        : showErrors && !String(form.name ?? '').trim()
                          ? 'Display name is required'
                          : undefined)
                }
                error={
                  !!skillNameDuplicateError
                  || (showErrors && (
                    !!errors.name
                    || !String(form.name ?? '').trim()
                  ))
                }
                autoFocus={!editing && !displayNameLocked}
                slotProps={displayNameLocked ? { input: { readOnly: true } } : undefined}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="Code"
              value={form.code}
              onChange={(e) => {
                setCodeAuto(false);
                const raw = e.target.value.replace(/\s+/g, '_');
                const code = FOOD_AUTO_CODE_TYPES.has(currentType) || DELIVERY_AUTO_CODE_TYPES.has(currentType) || commonManaged
                  ? raw.toLowerCase().replace(/[^a-z0-9_]/g, '')
                  : raw.toUpperCase().replace(/[^A-Z0-9_]/g, '');
                setField({
                  code,
                  metadata: autoServiceCodeField
                    ? { ...form.metadata, serviceCode: code }
                    : form.metadata,
                });
              }}
              disabled={!!editing}
              helperText={
                (codeDuplicateError || (showErrors && errors.code))
                  ? (codeDuplicateError || errors.code)
                  : (showErrors && !String(form.code ?? '').trim())
                    ? 'Code is required'
                  : editing
                    ? 'Code cannot be changed'
                    : currentType === 'food_variants'
                      ? 'Unique variant code — auto-filled from name (replaces Food Item Code)'
                    : currentType === 'preparation_times'
                      ? 'Auto-filled from minutes — e.g. prep_15'
                    : supportsAutoCode && codeAuto
                      ? (FOOD_AUTO_CODE_TYPES.has(currentType) || DELIVERY_AUTO_CODE_TYPES.has(currentType) || commonManaged
                        ? 'Auto ON — code is read-only and filled from the name (lowercase snake_case). Turn Auto OFF to type manually, or click the wand icon to regenerate.'
                        : 'Auto-filled from name — use the icon to regenerate or turn Auto off to type manually')
                      : supportsAutoCode
                        ? (FOOD_AUTO_CODE_TYPES.has(currentType) || DELIVERY_AUTO_CODE_TYPES.has(currentType) || commonManaged
                          ? `Auto OFF — type lowercase snake_case (e.g. ${masterCodePlaceholder(currentType)}), or click the wand icon to generate from the name`
                          : `Lowercase snake_case only (e.g. ${masterCodePlaceholder(currentType)})`)
                        : `Unique ID (e.g. ${masterCodePlaceholder(currentType)})`
              }
              error={
                !!codeDuplicateError
                || (showErrors && !!errors.code)
                || (showErrors && !editing && !String(form.code ?? '').trim())
              }
              slotProps={!editing && supportsAutoCode ? {
                input: {
                  readOnly: codeAuto,
                  endAdornment: (
                    <AutoCodeAdornment
                      auto={codeAuto}
                      disabled={
                        currentType === 'preparation_times'
                          ? !Number.isFinite(Number(form.metadata.minutes)) || Number(form.metadata.minutes) < 1
                          : currentType === 'restaurant_opening_hours'
                            ? !String(form.metadata.day ?? '').trim() && !form.name.trim()
                            : (FOOD_AUTO_CODE_TYPES.has(currentType) || DELIVERY_AUTO_CODE_TYPES.has(currentType))
                              ? false
                              : !form.name.trim()
                      }
                      onRegenerate={regenerateCodesFromName}
                      onAutoChange={(on) => {
                        setCodeAuto(on);
                        if (on) regenerateCodesFromName();
                      }}
                    />
                  ),
                },
              } : undefined}
            />
          </Grid>
          {hasBasicInfoImage && (
            <Grid item xs={12}>
              <MetadataFields
                fields={metaImageFields}
                values={form.metadata}
                onChange={setMeta}
                onPatchValues={patchMeta}
                masterTypeKey={currentType}
                errors={errors}
                showErrors={showErrors}
                liveMetaKeys={metadataLiveKeys}
                existingRecords={existingRecords}
                editingId={editingId}
                foodCategoryCode={form.code}
              />
            </Grid>
          )}
          {autoServiceCodeField && (
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label={autoServiceCodeField.label || 'Service Code'}
                value={String(form.metadata.serviceCode ?? (codeAuto ? form.code : '') ?? '')}
                onChange={(e) => {
                  setCodeAuto(false);
                  setMeta('serviceCode', e.target.value.toUpperCase().replace(/\s+/g, '_'));
                }}
                disabled={editing || codeAuto}
                helperText={
                  showErrors && errors['meta:serviceCode']
                    ? errors['meta:serviceCode']
                    : editing
                      ? 'Service code cannot be changed'
                      : codeAuto
                        ? 'Auto-filled from name — use the icon to regenerate or turn Auto off to type manually'
                        : autoServiceCodeField.helperText || 'Unique service identifier (e.g. LOCAL)'
                }
                error={showErrors && !!errors['meta:serviceCode']}
                InputProps={!editing ? {
                  endAdornment: (
                    <AutoCodeAdornment
                      auto={codeAuto}
                      disabled={!form.name.trim()}
                      onRegenerate={regenerateCodesFromName}
                      onAutoChange={(on) => {
                        setCodeAuto(on);
                        if (on) regenerateCodesFromName();
                      }}
                    />
                  ),
                } : undefined}
              />
            </Grid>
          )}
          {parentInBasicInfo && parentCategoryField && (
            <Grid item xs={12}>
              <SearchableMasterSelect
                label={parentCategoryField.label || 'Parent Category'}
                masterType={parentCategoryField.masterType || 'food_categories'}
                value={String(form.metadata.parentCategoryCode ?? '')}
                onChange={(code) => {
                  const patch: Partial<MasterFormState> = {
                    metadata: { ...form.metadata, parentCategoryCode: code },
                  };
                  if (!editing && code) {
                    patch.sortOrder = currentType === 'grocery_sub_categories'
                      ? getNextGrocerySubCategorySortOrder(existingRecords, code)
                      : getNextFoodSubCategorySortOrder(existingRecords, code);
                  } else if (!editing && !code) {
                    patch.sortOrder = 0;
                  }
                  setField(patch);
                }}
                required={parentCategoryField.required}
                helperText={parentCategoryField.helperText}
                error={showErrors && !!errors['meta:parentCategoryCode']}
                errorText={showErrors ? errors['meta:parentCategoryCode'] : undefined}
                optionLabelMode="name"
              />
            </Grid>
          )}
          {attributeParentInBasicInfo && (
            <Grid item xs={12}>
              <ParentMasterSelect
                label="Parent Attribute"
                value={form.parentId}
                options={parentOptions}
                required
                helperText="Select the attribute this value belongs to — listed in Food Attribute Master sort order"
                error={showErrors && (!!errors['meta:attributeCode'] || !!errors.parentId)}
                errorText={showErrors ? (errors['meta:attributeCode'] || errors.parentId) : undefined}
                onChange={(parentId, selected) => {
                  const patch: Partial<MasterFormState> = {
                    parentId,
                    metadata: { ...form.metadata, attributeCode: selected?.code || '' },
                  };
                  if (selected?.code && !editing) {
                    patch.sortOrder = getNextFoodAttributeValueSortOrder(existingRecords, selected.code);
                  }
                  setField(patch);
                }}
              />
              {!parentOptions.length && (
                <Typography variant="caption" color="warning.main" display="block" mt={0.75}>
                  No food attributes found — add records in Food Attribute Master first.
                </Typography>
              )}
            </Grid>
          )}
          {!hideTopLevelDescription && (
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => {
                  const raw = e.target.value;
                  const description = validatedDescription
                    ? limitConsecutiveRepeatedCharacters(raw)
                    : raw;
                  setField({ description });
                }}
                placeholder={
                  currentType === 'delivery_partner_pricing'
                    ? 'Base ₹35 for first 3 km · ₹6/km after · min guarantee ₹50'
                    : 'Optional admin notes'
                }
                multiline
                minRows={2}
                error={!!descriptionError || (showErrors && !!errors.description)}
                helperText={
                  descriptionError
                  || (showErrors && errors.description)
                  || (currentType === 'delivery_partner_pricing'
                    ? 'Summarize pricing rates — repeated characters like ddddd are not allowed'
                    : (currentType === 'delivery_slots'
                      || currentType === 'packaging_types'
                      || currentType === 'grocery_store_types'
                      || currentType === 'grocery_brands'
                      || currentType === 'grocery_categories'
                      || currentType === 'grocery_sub_categories'
                      || currentType === 'warehouses'
                      || currentType === 'product_variants'
                      || currentType === 'weight_types'
                      || currentType === 'suppliers'
                      || currentType === 'stock_alerts'
                      || currentType === 'grocery_tax_groups'
                      || currentType === 'grocery_product_images'
                      || currentType === 'grocery_availability_schedules')
                      ? 'Optional notes — repeated characters (e.g. aaaa) or consecutive duplicate words are not allowed'
                      : validatedDescription
                        ? 'Avoid repeated characters or consecutive duplicate words'
                        : undefined)
                }
              />
            </Grid>
          )}
          {!hideOpeningHourSortOrder && !hideAutoSortOrderOnCreate && (
          <Grid item xs={12} sm={hideStatusToggle || forceHideActiveStatus ? 12 : 6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Sort order"
              value={sortOrderFieldValue}
              onChange={(e) => {
                if (sortOrderFieldDisabled) return;
                const raw = e.target.value;
                if (strictIntegerSortOrder && (/[-+,eE.]/.test(raw))) return;
                const next = raw === '' ? 0 : Number(raw);
                if (strictIntegerSortOrder && raw !== '' && (!Number.isInteger(next) || next < 1)) return;
                setField({ sortOrder: next || 0 });
              }}
              disabled={sortOrderFieldDisabled}
              helperText={
                (sortOrderDuplicateError || (showErrors && errors.sortOrder))
                  ? (sortOrderDuplicateError || errors.sortOrder)
                  : currentType === 'food_sub_categories' && !sortOrderParentCategoryCode && !editing
                    ? 'Select a parent category first — order is assigned automatically'
                    : currentType === 'grocery_sub_categories' && !sortOrderParentCategoryCode && !editing
                      ? 'Select a parent category first — order is assigned automatically'
                    : currentType === 'food_attribute_values' && !sortOrderAttributeCode && !editing
                      ? 'Select a parent attribute first — order is assigned automatically'
                    : !editing && effectiveLockedSortOrder != null
                      ? `Automatically set to ${effectiveLockedSortOrder} — the next number in sequence`
                      : strictIntegerSortOrder
                        ? uniqueSortOrder
                          ? parentScopedSortOrder
                            ? currentType === 'food_sub_categories' || currentType === 'grocery_sub_categories'
                              ? 'Whole numbers only — must be unique within the selected parent category'
                              : currentType === 'service_categories'
                                ? 'Whole numbers only — must be unique among sibling categories'
                              : currentType === 'food_attribute_values'
                                ? 'Whole numbers only — must be unique within the selected parent attribute'
                                : 'Whole numbers only — must be unique within the selected ride attribute'
                            : 'Whole numbers only — must be unique across records'
                          : 'Whole numbers only — no decimals'
                        : 'Lower numbers appear first'
              }
              error={!!sortOrderDuplicateError || (showErrors && !!errors.sortOrder)}
              inputProps={{
                min: 1,
                max: 9999,
                step: 1,
                readOnly: sortOrderFieldDisabled ? true : undefined,
              }}
            />
          </Grid>
          )}
          {showFoodStatusSelect && (
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={
                  form.isActive !== false && String(form.metadata.status ?? 'active').toLowerCase() !== 'inactive'
                    ? 'active'
                    : 'inactive'
                }
                disabled={editingDeprecated}
                onChange={(e) => {
                  const isActive = e.target.value === 'active';
                  setField({
                    isActive,
                    metadata: {
                      ...form.metadata,
                      status: e.target.value,
                    },
                  });
                }}
                helperText="Set whether this category is active or inactive"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          )}
          {showActiveSwitch && (
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={form.isActive !== false}
                    disabled={editingDeprecated}
                    onChange={(e) => {
                      const isActive = e.target.checked;
                      setField({
                        isActive,
                        metadata: {
                          ...form.metadata,
                          ...(schema?.metadataFields?.some((f) => f.key === 'status')
                            ? { status: isActive ? 'active' : 'inactive' }
                            : {}),
                        },
                      });
                    }}
                  />
                )}
                label="Active"
              />
            </Grid>
          )}
        </FormGrid>
      </FormSection>

      {showParentMasterSection && (
        <FormSection
          title={currentType === 'service_categories' ? 'Category hierarchy' : 'Parent master'}
          description={
            parentOptional
              ? 'Leave empty for a primary category. Select a primary to create a sub-category.'
              : 'Search and select the parent record.'
          }
          divider
        >
          <ParentMasterSelect
            label={parentLabel}
            value={form.parentId}
            options={parentOptions}
            required={parentRequired}
            helperText={
              parentOptional
                ? 'Primary categories have no parent; sub-categories must select one'
                : undefined
            }
            error={showErrors && !!errors.parentId}
            errorText={showErrors ? errors.parentId : undefined}
            onChange={(parentId, selected) => {
              const legacyKey = LEGACY_PARENT_META_KEY[currentType];
              const patch: Partial<MasterFormState> = {
                parentId,
                country: selected?.code && parentMasterType === 'countries' ? selected.code : form.country,
                ...(legacyKey && selected?.code
                  ? { metadata: { ...form.metadata, [legacyKey]: selected.code } }
                  : {}),
              };
              if (currentType === 'food_attribute_values' && selected?.code && !editing) {
                patch.sortOrder = getNextFoodAttributeValueSortOrder(existingRecords, selected.code);
              }
              setField(patch);
            }}
          />
          {!parentOptions.length && (
            <Typography variant="caption" color="warning.main" display="block" mt={0.75}>
              {parentOptional
                ? 'No primary categories yet — create one without a parent first.'
                : `No parent records found. Add ${parentMasterType?.replace(/_/g, ' ')} masters first.`}
            </Typography>
          )}
        </FormSection>
      )}

      {hasGeoFields && (
        <FormSection title="Location" divider>
          <FormGrid>
            {schema?.geoFields?.includes('country') && (
              <Grid item xs={12} sm={6}>
                <SearchableMasterSelect
                  label="Country"
                  masterType={GEO_FIELD_SOURCE.country || 'countries'}
                  value={form.country}
                  onChange={(code) => setField({ country: code })}
                  required={currentType === 'warehouses'}
                  error={showErrors && !!errors.country}
                  errorText={showErrors ? errors.country : undefined}
                />
              </Grid>
            )}
          </FormGrid>
          {isTransportHubType(currentType) && (
            <HubCoordinatePicker
              masterType={currentType}
              lat={Number(form.metadata.centerLat) || undefined}
              lng={Number(form.metadata.centerLng) || undefined}
              onChange={(lat, lng) => onChange({
                ...form,
                metadata: { ...form.metadata, centerLat: lat, centerLng: lng },
              })}
            />
          )}
        </FormSection>
      )}

      {propertiesMetaFields.length > 0 && (
        <FormSection title="Properties" divider>
          {currentType === 'restaurant_opening_hours' && !editing && !allOpeningDaysTaken && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Select a weekday first — even when closed all day, each record is for one specific day.
              Sort order is set automatically (Mon=1 … Sun=7).
            </Alert>
          )}
          {currentType === 'delivery_partner_pricing' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Partner earnings formula
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Earnings = Base Amount + (km beyond Base KM × Remaining KM Rate) + Pickup Charge + Drop Charge
                (+ waiting minutes × Waiting Charge). If that total is below Minimum Guarantee, the guarantee applies.
              </Typography>
              <Typography variant="body2">
                {formatDeliveryPartnerPricingExample(form.metadata)}
              </Typography>
            </Alert>
          )}
          {currentType === 'delivery_partner_pricing' && (() => {
            const guaranteeHint = formatDeliveryPartnerPricingGuaranteeHint(form.metadata);
            if (!guaranteeHint) return null;
            return (
              <Alert severity={guaranteeHint.severity} sx={{ mb: 2 }} data-guarantee-validation>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Minimum Guarantee vs Base Amount
                </Typography>
                <Typography variant="body2">
                  {guaranteeHint.message}
                </Typography>
              </Alert>
            );
          })()}
          {currentType === 'delivery_incentives' && (
            <Alert severity="info" sx={{ mb: 2 }} data-incentive-explanation>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                How this incentive is applied
              </Typography>
              <Typography variant="body2">
                {formatDeliveryIncentiveExplanation(form.metadata)}
              </Typography>
            </Alert>
          )}
          {currentType === 'delivery_partners_tiers' && (
            <Alert severity="info" sx={{ mb: 2 }} data-partner-tier-explanation>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                How partner tiers are assigned
              </Typography>
              <Typography variant="body2">
                {formatDeliveryPartnerTierExplanation(form.metadata)}
              </Typography>
            </Alert>
          )}
          {currentType === 'substitution_rules' && (
            <Alert
              severity={String(form.metadata.allowSubstitute) === 'false' || form.metadata.allowSubstitute === false ? 'warning' : 'info'}
              sx={{ mb: 2 }}
              data-substitution-rule-explanation
            >
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Mobile visibility
              </Typography>
              <Typography variant="body2">
                {formatSubstitutionRuleExplanation(form.metadata)}
              </Typography>
            </Alert>
          )}
          <MetadataFields
            fields={propertiesMetaFields}
            values={form.metadata}
            onChange={setMeta}
            onPatchValues={patchMeta}
            masterTypeKey={currentType}
            errors={errors}
            showErrors={showErrors}
            liveMetaKeys={metadataLiveKeys}
            existingRecords={existingRecords}
            editingId={editingId}
            foodCategoryCode={form.code}
          />
        </FormSection>
      )}
      <Box sx={{ height: 16, flexShrink: 0 }} aria-hidden />
    </FormDrawer>
  );
}
